import {
  DTOptimizationNode,
  OptimizationNodeOutputData,
} from 'common/src/modules/decisionTree/decisionTree.types';
import {
  type CampaignOptimizationData,
  OptimizeAcrossName,
  OptimizeAcrossValue,
  OptimizeByName,
  OptimizeByValue,
} from 'common/src/modules/optimization/optimization.types';
import getMatchingEnumValue from 'common/src/utils/typescriptUtils/getMatchingEnumValue';
import {formatOutputHeader, getBaseHeader, getDestinationPhrase, sortOutputsByType} from './utils';

const CONVERSION_OPTIMIZE_BY_VALUES = [
  OptimizeByValue.Conversions,
  OptimizeByValue.ClickThroughConversions,
  OptimizeByValue.ExternalConversions,
];

function isConversionOptimizeBy(optimizeBy: number): boolean {
  return CONVERSION_OPTIMIZE_BY_VALUES.includes(optimizeBy as OptimizeByValue);
}

function formatDropCondition(
  condition: CampaignOptimizationData['dropConditions'][number],
): string {
  return [
    `Minimum Confidence Level: ${condition.minConfidenceLevel}%`,
    `Minimum Days: ${condition.minDays}`,
    `Minimum Impressions: ${condition.minImpressions}`,
    `Minimum Performance Lift: ${condition.minPerformanceLift}%`,
    `Minimum Results: ${condition.minResults}`,
  ].join(', ');
}

export function optimizationCardToText(
  card: DTOptimizationNode,
  mode: 'short' | 'full',
  fallbackCreativeNodeId?: number,
): string {
  const header = getBaseHeader(card);
  const sortedOutputs = sortOutputsByType(card.outputs);

  if (mode === 'short') {
    const optimizeByName =
      getMatchingEnumValue(
        OptimizeByValue,
        OptimizeByName,
        card.configuration?.optimizeBy as OptimizeByValue,
      ) ?? '';

    const purpose = `Purpose: Optimize output selection by '${optimizeByName}' rate`;

    const outputLines = sortedOutputs.map((output: OptimizationNodeOutputData, index) => {
      const outputHeader = formatOutputHeader(index, output);
      const dest = getDestinationPhrase(output.nodeId, fallbackCreativeNodeId, output.type);

      return `${outputHeader} ${dest}`;
    });

    return [header, purpose, ...outputLines].join('\n');
  }

  const config = card.configuration;
  const optimizeByName =
    getMatchingEnumValue(OptimizeByValue, OptimizeByName, config?.optimizeBy as OptimizeByValue) ??
    '';
  const optimizeAcrossName =
    getMatchingEnumValue(
      OptimizeAcrossValue,
      OptimizeAcrossName,
      config?.optimizeGroup as OptimizeAcrossValue,
    ) ?? '';

  const outputLines = sortedOutputs.map((output: OptimizationNodeOutputData, index) => {
    const outputHeader = formatOutputHeader(index, output);
    const dest = getDestinationPhrase(output.nodeId, fallbackCreativeNodeId, output.type);

    return `${outputHeader} ${dest}`;
  });

  const lines = [
    header,
    `Optimize By: ${optimizeByName}`,
    `Optimize Across: ${optimizeAcrossName}`,
    ...outputLines,
  ];

  if (isConversionOptimizeBy(config?.optimizeBy ?? 0)) {
    if (config?.areAllAttributionSignalsSelected) {
      lines.push('For all attribution signals');
    } else if (config?.attributionSignalIds?.length) {
      lines.push('Only for attribution signals:');

      for (const id of config.attributionSignalIds) {
        lines.push(`- ID ${id}`);
      }
    }
  }

  if (config?.dropConditions?.length) {
    lines.push('Drop Variant Conditions:');

    for (const condition of config.dropConditions) {
      lines.push(formatDropCondition(condition));
    }
  }

  return lines.join('\n');
}
